import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useSWR from 'swr';
import { shouldOpenUsageBillingPortal, type UsageBillingSnapshot } from '@kangaroo/shared';
import { SectionCard, colors } from '@kangaroo/ui';

import {
  currentUsageBillingKey,
  deactivateCurrentUsageBilling,
  fetchUsageBilling,
  openUsageBillingPortal,
  removeCurrentUsagePaymentMethods,
  redeemPartnerCode,
  startUsageBillingCheckout,
  syncUsageBilling,
} from '../../features/billing';

type TeamPlanUsagePanelProps = {
  billingSnapshot?: UsageBillingSnapshot;
  onManageBilling?: () => Promise<void>;
  defaultExpanded?: boolean;
  allowPaymentManagement?: boolean;
  billingNotice?: string | null;
  checkoutSessionId?: string | null;
  onClearBillingParams?: () => void;
  onRefresh?: () => Promise<void>;
};

function money(value: number | null) {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function billingErrorMessage(error: unknown) {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) return response.data.message;
  }
  return error instanceof Error ? error.message : 'Unable to open billing.';
}

export function TeamPlanUsagePanel({
  billingSnapshot, onManageBilling,
  defaultExpanded = true,
  allowPaymentManagement = false,
  billingNotice,
  checkoutSessionId,
  onClearBillingParams,
  onRefresh,
}: TeamPlanUsagePanelProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [partnerCode, setPartnerCode] = React.useState('');
  const { data, isLoading, mutate } = useSWR(billingSnapshot ? null : currentUsageBillingKey, fetchUsageBilling);
  const usage = billingSnapshot ?? data;

  React.useEffect(() => {
    if (billingNotice !== 'success' || !checkoutSessionId) return;
    void (async () => {
      try {
        await syncUsageBilling(checkoutSessionId);
        await Promise.all([mutate(), onRefresh?.()]);
        onClearBillingParams?.();
      } catch (cause) {
        setError(billingErrorMessage(cause));
      }
    })();
  }, [billingNotice, checkoutSessionId, mutate, onClearBillingParams, onRefresh]);

  async function manageBilling() {
    if (!(allowPaymentManagement && usage?.canManageBilling)) return;
    if (onManageBilling) { await onManageBilling(); return; }
    setError(null);
    setLoading(true);
    try {
      const { url } = shouldOpenUsageBillingPortal(usage.hasStripeSubscription) || (usage.partnerAccess && usage.hasPaymentMethod)
        ? await openUsageBillingPortal()
        : await startUsageBillingCheckout();
      window.location.href = url;
    } catch (cause) {
      setError(billingErrorMessage(cause));
      setLoading(false);
    }
  }

  async function deactivateBilling() {
    if (!window.confirm(usage?.partnerAccess
      ? 'Deactivate usage billing at the end of the current billing period? Uploads remain covered while partner access is active.'
      : 'Deactivate usage billing at the end of the current billing period? Uploads will pause when the subscription ends.')) return;
    setLoading(true);
    setError(null);
    try {
      await deactivateCurrentUsageBilling();
      await Promise.all([mutate(), onRefresh?.()]);
    } catch (cause) {
      setError(billingErrorMessage(cause));
    } finally {
      setLoading(false);
    }
  }

  async function removeCard() {
    if (!window.confirm('Remove every saved card from this billing account? Uploads requiring payment will remain paused.')) return;
    setLoading(true);
    setError(null);
    try {
      await removeCurrentUsagePaymentMethods();
      await Promise.all([mutate(), onRefresh?.()]);
    } catch (cause) {
      setError(billingErrorMessage(cause));
    } finally {
      setLoading(false);
    }
  }

  async function applyPartnerCode() {
    if (!(allowPaymentManagement && usage?.canManageBilling) || partnerCode.trim().length < 4) return;
    setLoading(true);
    setError(null);
    try {
      await redeemPartnerCode(partnerCode);
      setPartnerCode('');
      await Promise.all([mutate(), onRefresh?.()]);
    } catch (cause) {
      setError(billingErrorMessage(cause));
    } finally {
      setLoading(false);
    }
  }

  if (usage && !usage.canManageBilling) {
    return <SectionCard><Stack spacing={2}>
      <Typography variant="h6">Upload access</Typography>

      <Alert severity={usage.uploadAllowed ? 'success' : 'warning'}>{usage.uploadAllowed ? 'Uploads are available.' : 'New uploads are paused. Ask the billing owner to restore access.'}</Alert>
    </Stack></SectionCard>;
  }

  if (usage?.billingMode === 'fixed_monthly') {
    return <SectionCard><Stack spacing={2}>
      <Typography variant="h6">Fixed monthly</Typography>

      {usage.canManageBilling && usage.scheduledUsageStartsAt ? <Alert severity="info">Usage starts · {new Date(usage.scheduledUsageStartsAt).toUTCString()}</Alert> : null}
      {usage.canManageBilling && usage.fixedMonthlyAmountCents != null ? <Typography>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(usage.fixedMonthlyAmountCents / 100)} per month</Typography> : null}
      {usage.canManageBilling && usage.collectionMethod ? <Typography>{usage.collectionMethod === 'send_invoice' ? 'Emailed invoice' : 'Automatic card payment'}</Typography> : null}
      <Alert severity={usage.uploadAllowed ? 'success' : 'warning'}>{usage.uploadAllowed ? 'Uploads included while your fixed plan is in good standing.' : 'New uploads are paused while payment needs attention.'}</Alert>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {allowPaymentManagement && usage.canManageBilling ? <Button disabled={loading} onClick={manageBilling}>Manage billing on Stripe</Button> : <Button href="/billing">Open Billing</Button>}
    </Stack></SectionCard>;
  }

  const includedPercent = usage
    ? Math.min(100, (usage.currentPeriodSessions / Math.max(1, usage.monthlyIncludedSessions)) * 100)
    : 0;
  const statusLabel = usage?.partnerAccess
    ? 'Partner access'
    : usage?.hasActiveSubscription
      ? 'Usage plan active'
      : usage?.hasPaymentMethod
        ? 'Card on file'
        : usage?.lifetimeFreeRemaining
          ? `${usage.lifetimeFreeRemaining} free left`
          : 'Card required';

  return (
    <SectionCard contentSx={{ p: 0, '&:last-child': { pb: 0 } }}>
      <Box
        onClick={() => setExpanded((value) => !value)}
        sx={{ px: 2.5, pt: 2, pb: expanded ? 1 : 2, cursor: 'pointer', userSelect: 'none' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, minHeight: 28 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, lineHeight: '28px' }}>
            {usage?.partnerAccess ? 'Account access' : 'Usage billing'}
          </Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 28, gap: 0.5 }} onClick={(event) => event.stopPropagation()}>
            {usage ? (
              <Chip
                size="small"
                label={statusLabel}
                color={usage.partnerAccess || usage.hasActiveSubscription || usage.hasPaymentMethod ? 'success' : 'warning'}
                sx={{ height: 24, fontWeight: 800, '& .MuiChip-label': { px: 1, fontSize: '0.68rem' } }}
              />
            ) : null}
            <IconButton size="small" onClick={() => setExpanded((value) => !value)} aria-label={expanded ? 'Collapse billing' : 'Expand billing'}>
              <ExpandMoreIcon sx={{ fontSize: 20, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={1.5} sx={{ px: 2.5, pb: 2.25 }} onClick={(event) => event.stopPropagation()}>
          {!allowPaymentManagement ? <Button href="/billing">Open Billing</Button> : null}
          {isLoading || !usage ? <CircularProgress size={22} /> : (
            <>

              {billingNotice === 'cancel' ? <Alert severity="info">Checkout was canceled. No changes were made.</Alert> : null}
              {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
              {usage.hasPaymentMethod && !usage.hasActiveSubscription && !usage.partnerAccess ? (
                <Alert severity="info" sx={{ fontSize: '0.76rem' }}>
                  {usage.hasStripeSubscription ? 'Your card is saved. Refresh billing to confirm the subscription status.' : 'Your card is saved. Activate usage billing to enable uploads beyond the free allowance.'}
                </Alert>
              ) : null}
              {usage.cancelAtPeriodEnd ? (
                <Alert severity="warning" sx={{ fontSize: '0.76rem' }}>Usage billing will deactivate at the end of the current billing period. {usage.partnerAccess ? 'Uploads remain covered while partner access is active.' : ''}</Alert>
              ) : null}

              <Box>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>
                  Account
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: '0.92rem', fontWeight: 800 }}>{usage.ownerLabel}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.74rem' }}>
                  {usage.partnerAccess
                    ? 'Covered by partner access'
                    : usage.ownerKind === 'team'
                      ? 'Team billing account'
                      : 'Personal billing account'}
                </Typography>
              </Box>

              {usage.partnerAccess ? (
                <Alert
                  severity="success"
                  sx={{
                    alignItems: 'flex-start',
                    border: '1px solid',
                    borderColor: 'success.light',
                    '& .MuiAlert-message': { width: '100%' },
                  }}
                >
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 900 }}>
                    Complimentary access is active
                  </Typography>
                  <Typography sx={{ mt: 0.35, fontSize: '0.75rem', lineHeight: 1.45 }}>
                    {usage.partnerProgramLabel
                      ? `${usage.partnerProgramLabel} covers this account. `
                      : 'A partner program covers this account. '}
                    Uploads and sessions cost $0 while this access is active. No payment method is required.
                  </Typography>
                  <Typography sx={{ mt: 0.8, fontSize: '0.72rem', fontWeight: 800 }}>
                    {usage.currentPeriodSessions} session{usage.currentPeriodSessions === 1 ? '' : 's'} this month · $0 session usage
                  </Typography>
                </Alert>
              ) : (
                <>
                  <Box
                    sx={{
                      p: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'rgba(255,255,255,0.02)',
                    }}
                  >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box>
                    <Typography sx={{ fontSize: '0.76rem', fontWeight: 800 }}>First 5 sessions ever</Typography>
                    <Typography sx={{ mt: 0.2, color: 'text.secondary', fontSize: '0.7rem' }}>
                      One-time free allowance
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`${Math.min(usage.lifetimeSessions, usage.lifetimeFreeSessions)} of ${usage.lifetimeFreeSessions} used`}
                    color={usage.lifetimeFreeRemaining === 0 ? 'default' : 'success'}
                    sx={{ height: 24, fontWeight: 800, '& .MuiChip-label': { px: 1, fontSize: '0.68rem' } }}
                  />
                </Stack>
                <Typography sx={{ mt: 0.9, fontSize: '0.72rem', fontWeight: 700, color: usage.lifetimeFreeRemaining === 0 ? 'text.secondary' : 'success.main' }}>
                  {usage.lifetimeFreeRemaining === 0
                    ? 'Lifetime free sessions used'
                    : `${usage.lifetimeFreeRemaining} lifetime-free session${usage.lifetimeFreeRemaining === 1 ? '' : 's'} remaining`}
                </Typography>
                  </Box>

              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.65 }}>
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 700 }}>Sessions this month</Typography>
                  <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
                    {usage.currentPeriodSessions} / {usage.monthlyIncludedSessions} included
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={includedPercent}
                  sx={{ height: 7, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.06)', '& .MuiLinearProgress-bar': { bgcolor: colors.primary[400], borderRadius: 999 } }}
                />
                {(usage.currentPeriodBillableSessions ?? 0) > 0 ? (
                  <Typography sx={{ mt: 0.65, color: 'text.secondary', fontSize: '0.72rem' }}>
                    {usage.currentPeriodBillableSessions} additional session{usage.currentPeriodBillableSessions === 1 ? '' : 's'} at {money(usage.additionalSessionUsd)} each
                  </Typography>
                ) : null}
              </Box>

              <Divider />
              <Stack spacing={0.8}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>Active month · first 5 sessions</Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                    {usage.currentPeriodSessions > 0 ? money(usage.baseMonthlyUsd) : '$0 until first upload'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>Additional sessions</Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                    {money((usage.currentPeriodBillableSessions ?? 0) * usage.additionalSessionUsd)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.4 }}>
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 900 }}>Estimated this month</Typography>
                  <Typography sx={{ color: colors.primary[300], fontSize: '1.05rem', fontWeight: 900 }}>
                    {money(usage.currentPeriodEstimatedTotalUsd)}
                  </Typography>
                </Stack>
              </Stack>


                </>
              )}

              {(allowPaymentManagement && usage.canManageBilling) && !usage.partnerAccess ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                  <TextField
                    label="Partner code"
                    value={partnerCode}
                    onChange={(event) => setPartnerCode(event.target.value.toUpperCase())}
                    placeholder="QA277-RETRY-A"
                    size="small"
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    disabled={loading || partnerCode.trim().length < 4}
                    onClick={() => void applyPartnerCode()}
                    sx={{ textTransform: 'none', fontWeight: 800, flexShrink: 0, py: 0.85 }}
                  >
                    Apply code
                  </Button>
                </Stack>
              ) : null}

              {(allowPaymentManagement && usage.canManageBilling) && usage.stripeAvailable && (!usage.partnerAccess || usage.hasStripeSubscription || usage.hasPaymentMethod) ? (
                <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                  {usage.collectionMethod === 'send_invoice' ? 'Emailed invoices · Manage on Stripe' : 'Manage your card securely on Stripe.'}
                </Typography>
              ) : null}
              {(allowPaymentManagement && usage.canManageBilling) && usage.stripeAvailable && (!usage.partnerAccess || usage.hasStripeSubscription || usage.hasPaymentMethod) ? (
                <Button variant="contained" fullWidth disabled={loading} onClick={() => void manageBilling()} sx={{ textTransform: 'none', fontWeight: 800 }}>
                  {loading ? <CircularProgress size={18} color="inherit" /> : usage.collectionMethod === 'send_invoice' && usage.hasStripeSubscription ? 'Manage invoices' : usage.partnerAccess ? 'Manage existing billing' : !usage.hasStripeSubscription && usage.hasPaymentMethod ? 'Activate usage billing' : usage.hasPaymentMethod ? 'Manage payment method' : 'Add payment method'}
                </Button>
              ) : null}
              {(allowPaymentManagement && usage.canManageBilling) && usage.hasActiveSubscription && !usage.cancelAtPeriodEnd ? (
                <Button color="error" variant="outlined" fullWidth disabled={loading} onClick={() => void deactivateBilling()} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Deactivate at period end
                </Button>
              ) : null}
              {(allowPaymentManagement && usage.canManageBilling) && usage.hasPaymentMethod && !usage.hasActiveSubscription ? (
                <Button color="error" variant="text" fullWidth disabled={loading} onClick={() => void removeCard()} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Remove saved card
                </Button>
              ) : null}
            </>
          )}
        </Stack>
      </Collapse>
    </SectionCard>
  );
}
